import { Plus } from "lucide-react";
import { formatDate } from "@/lib/format";
// web/src/pages/enquiries/EnquiriesListPage.tsx
//
// E-1 — Enquiries list page.
//
// Canonical chrome (Plan IV Task 4):
// - search: backend ?q= filter on Description (server-side, see
//   EnquiryService.ListAsync — Description.Contains(q)).
// - filterChips: single-select view (All / Open / Won / Lost).
//   "Open" spans three statuses (New/InQuote/Quoted) but the backend
//   EnquiryListQuery.Status is a single string — we omit the status param
//   for the Open view and post-filter the page client-side via useMemo.
//   Won/Lost map 1:1 onto the single-string backend filter. Flagged in
//   the Plan IV runbook as a known limitation.
// - itemCount: wired from data?.totalCount.
//
// PAGINATION CAVEAT: Backend `EnquiryListQuery.Status` accepts a single
// string only (not string[]), so the "Open" view fetches the unfiltered
// page from the backend and filters client-side to OPEN_STATUSES. As a
// result, under pagination: itemCount reflects the unfiltered backend
// total (not the post-filter count), and the visible row count may be
// less than pageSize. Subsequent pages may also have gaps or empty
// pages until the user reaches enquiries that match.
//
// Fix path: extend EnquiryListQuery.Status to accept string[] (see
// Plan IV runbook). Until then, "Open" is a best-effort filter that
// degrades under pagination.
//
// Drops: legacy filterBar (multi-select status pill row + 4-column search
// grid wrapping a single Search Input). The single-select view chips
// subsume the multi-select status pills per Plan IV §P1.
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { enquiriesApi, type EnquiryListItemDto, type ListEnquiriesParams, type EnquiryStatus } from "@/lib/enquiries";
import { EnquiryStatusBadge } from "@/components/enquiries/enquiry-status-badge";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn, type SortDirection } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { NewEnquiryDialog } from "@/components/enquiries/NewEnquiryDialog";
import { useAuth } from "@/app/auth-context";

// ---------------------------------------------------------------------------
// View chips — single-select. "Open" is a client-side post-filter; Won/Lost
// map to a single backend status= value.
// ---------------------------------------------------------------------------

type ViewChipId = "all" | "open" | "won" | "lost";

const VIEW_CHIP_OPTIONS: ReadonlyArray<{ id: ViewChipId; label: string }> = [
  { id: "all",  label: "All" },
  { id: "open", label: "Open" },
  { id: "won",  label: "Won" },
  { id: "lost", label: "Lost" },
];

// Statuses comprising the "Open" view (not yet Won/Lost/Abandoned).
const OPEN_STATUSES: ReadonlySet<EnquiryStatus> = new Set<EnquiryStatus>([
  "New",
  "InQuote",
  "Quoted",
]);

function applyView(view: ViewChipId): { status?: EnquiryStatus } {
  switch (view) {
    case "won":  return { status: "Won" };
    case "lost": return { status: "Lost" };
    case "open":
    case "all":
    default:
      return {};
  }
}

// ---------------------------------------------------------------------------
// EnquiriesListPage

export function EnquiriesListPage() {
  const { permissions } = useAuth();
  const [searchText, setSearchText] = useState("");
  const [selectedView, setSelectedView] = useState<ViewChipId>("all");
  const [pageIndex, setPageIndex] = useState(0);
  const [newEnquiryOpen, setNewEnquiryOpen] = useState(false);
  const navigate = useNavigate();

  const params = useMemo<ListEnquiriesParams>(() => {
    const view = applyView(selectedView);
    return {
      status: view.status,
      q: searchText.trim() || undefined,
      page: pageIndex,
      pageSize: 50,
    };
  }, [selectedView, searchText, pageIndex]);

  const { data, isLoading } = useQuery({
    queryKey: ["enquiries", params],
    queryFn: () => enquiriesApi.list(params),
  });

  // "Open" view client-side post-filters the page slice to New/InQuote/Quoted.
  // Backend EnquiryListQuery.Status is single-string only, so we can't combine
  // three statuses server-side; total count reflects the unfiltered page.
  const filteredRows = useMemo(() => {
    const rows = data?.items ?? [];
    return selectedView === "open"
      ? rows.filter((r) => OPEN_STATUSES.has(r.status))
      : rows;
  }, [data, selectedView]);

  // Sort state — client-side only; enquiriesApi.list has no `sort` param.
  // Sort applies to the (already client-filtered) current page rows.
  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection } | null>(null);

  const visibleRows = useMemo(() => {
    if (!sort) return filteredRows;
    const dir = sort.direction === "asc" ? 1 : -1;
    const valueFor = (r: EnquiryListItemDto): string | number | null => {
      switch (sort.columnId) {
        case "id":          return r.enquiryId;
        case "description": return r.description ?? null;
        case "client":      return r.clientName ?? null;
        case "owner":       return r.ownerEmployeeName ?? null;
        case "status":      return r.status;
        case "source":      return r.sourceSystem;
        case "created":     return r.createdOn ?? null;
        default:            return null;
      }
    };
    return [...filteredRows].sort((a, b) => {
      const va = valueFor(a);
      const vb = valueFor(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [filteredRows, sort]);

  const cols: DataGridColumn<EnquiryListItemDto>[] = [
    {
      id: "id",
      header: "#",
      sortable: true,
      accessor: (r) => (
        <Link to={`/enquiries/${r.enquiryId}`} className="font-medium text-primary hover:underline">
          {r.enquiryId}
        </Link>
      ),
    },
    {
      id: "description",
      header: "Description",
      sortable: true,
      accessor: (r) => r.description ?? "—",
    },
    {
      id: "client",
      header: "Client",
      sortable: true,
      accessor: (r) =>
        r.clientId ? (
          <Link to={`/clients/${r.clientId}`} className="underline text-primary hover:text-primary/80 transition-colors">
            {r.clientName ?? "—"}
          </Link>
        ) : (
          r.clientName ?? "—"
        ),
    },
    {
      id: "owner",
      header: "Owner",
      sortable: true,
      accessor: (r) => r.ownerEmployeeName ?? "—",
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      accessor: (r) => <EnquiryStatusBadge status={r.status} />,
    },
    {
      id: "source",
      header: "Source",
      sortable: true,
      accessor: (r) => r.sourceSystem,
    },
    {
      id: "created",
      header: "Created",
      sortable: true,
      accessor: (r) =>
        r.createdOn ? formatDate(r.createdOn) : "—",
    },
  ];

  return (
    <ListPage
      title="Enquiries"
      primaryAction={
        permissions.has("enquiries:write") ? (
          <IntentButton onClick={() => setNewEnquiryOpen(true)} aria-label="New enquiry">
            <Plus className="mr-1.5 h-4 w-4" /> Add Enquiry
          </IntentButton>
        ) : undefined
      }
      search={{
        value: searchText,
        onChange: (v) => { setSearchText(v); setPageIndex(0); },
        placeholder: "Search description…",
      }}
      filterChips={{
        options: VIEW_CHIP_OPTIONS,
        value: selectedView,
        onChange: (next) => { setSelectedView(next); setPageIndex(0); },
        "aria-label": "Enquiry view filter",
      }}
    >
      <DataGrid
        rows={visibleRows}
        columns={cols}
        rowKey={(r) => r.enquiryId}
        totalRows={data?.totalCount ?? 0}
        pageIndex={pageIndex}
        pageSize={50}
        loading={isLoading}
        emptyHeadline="No enquiries match these filters."
        onPageChange={setPageIndex}
        onSortChange={setSort}
        onRowClick={(r) => navigate(`/enquiries/${r.enquiryId}`)}
      />
      <NewEnquiryDialog open={newEnquiryOpen} onOpenChange={setNewEnquiryOpen} />
    </ListPage>
  );
}
